/**
 * useOrgTaskData Hook
 *
 * Fetches org-level tasks (crop_id IS NULL) for all plots and patches
 * within the current farm. Groups tasks by organization, pairing each
 * with its org info for use in the crop timeline as "org task rows".
 */

import { useState, useEffect } from 'react'
import { useOrgParams } from '~/common/use-org-params'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCropsContext } from '~/common/context/crops/crops-context'

/**
 * @param {Object} params
 * @param {boolean} params.skip - Skip fetching (e.g., in preview/override mode)
 * @returns {Object} { orgTaskGroups, loading, error }
 *   orgTaskGroups: [{ org: { id, name, organization_type }, tasks: [...] }]
 */
export const useOrgTaskData = ({ skip = false } = {}) => {
  const { farmId } = useOrgParams()
  const { refreshKey } = useCropsContext()
  const [orgTaskGroups, setOrgTaskGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchOrgTasks() {
      if (skip || !farmId) {
        setOrgTaskGroups([])
        setLoading(false)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Fetch all plots for this farm
        const { data: plots, error: plotsError } = await supabaseGetter.organizations.getPlots(farmId)
        if (plotsError) throw plotsError

        // Fetch all patches for each plot
        const patchResults = await Promise.all(
          plots?.map(plot => supabaseGetter.organizations.getPatches(plot.id))
        )
        const patches = patchResults?.flatMap(r => r.data || [])

        const allOrgIds = [
          farmId,
          ...plots.map(p => p.id),
          ...patches.map(p => p.id),
        ]

        // Fetch org-level tasks only (crop_id IS NULL)
        const { data: tasks, error: tasksError } = await supabaseGetter.tasks.getOrgTasksByOrganizations(allOrgIds)
        if (tasksError) throw tasksError

        if (!tasks || tasks.length === 0) {
          setOrgTaskGroups([])
          return
        }

        // Build an org lookup map from joined task.organization data
        const orgMap = new Map()
        tasks.forEach(task => {
          if (task.organization && !orgMap.has(task.organization_id)) {
            orgMap.set(task.organization_id, task.organization)
          }
        })

        // Group tasks by organization_id
        const tasksByOrg = new Map()
        tasks.forEach(task => {
          if (!tasksByOrg.has(task.organization_id)) {
            tasksByOrg.set(task.organization_id, [])
          }
          tasksByOrg.get(task.organization_id).push(task)
        })

        // Build orgTaskGroups array
        const groups = []
        tasksByOrg.forEach((orgTasks, orgId) => {
          const org = orgMap.get(orgId)
          if (org) {
            groups.push({ org, tasks: orgTasks })
          }
        })

        setOrgTaskGroups(groups)
      } catch (err) {
        setError(err.message)
        setOrgTaskGroups([])
      } finally {
        setLoading(false)
      }
    }

    fetchOrgTasks()
  }, [farmId, skip, refreshKey])

  return {
    orgTaskGroups,
    loading,
    error,
    invalidate: supabaseGetter.tasks.invalidate,
  }
}
